import React from 'react';

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * ⛔⛔ A FAILURE THAT DOES NOT THROW IS STILL A FAILURE — card P71.
 *
 * safeAsync caught thrown errors and nothing else, and the IPC bridge does not
 * throw: server.js:1340-1346 answers an unknown channel with HTTP **200** and
 * { error: true, code: "NO_HANDLER" }, and secureHandler refusals come back the
 * same way as { error: true, code: "ACCESS_DENIED" }. Both resolved cleanly, so
 * every CRM button wrapped in safeAsync reported nothing at all: no toast, no
 * alert, no console line. A staff member pressed Send and the screen agreed
 * with them that something had happened.
 *
 * ⚠ IT ALSO RETURNED NOTHING. The old body had no `return`, so callers written
 * as `const r = await safeAsync(...); if (r?.success)` could never be true even
 * when the call succeeded — the list refresh behind that branch never ran. Both
 * halves are fixed here: the result is returned, AND an { error: true } body is
 * surfaced the same way a throw is.
 *
 * ⛔ The error TEXT is whatever the server sent. These paths carry no patient
 * data — a NO_HANDLER names a channel, an ACCESS_DENIED names a permission —
 * and inventing a friendlier message would hide which of the two it was.
 */
export const safeAsync = (fn) => async (...args) => {
  try {
    const result = await fn(...args);
    if (result && typeof result === 'object' && result.error === true) {
      const detail = result.message || result.code || 'Unknown error';
      console.error('[CRM] handler returned an error', { code: result.code });
      alert('Error: ' + detail);
      return undefined;
    }
    return result;
  } catch (err) {
    console.error('[CRM]', err);
    alert('Error: ' + (err.message || 'Unknown error'));
    return undefined;
  }
};

export const sp = (v, fallback = {}) => { if (!v) return fallback; if (typeof v === 'object') return v; try { return JSON.parse(v); } catch { return fallback; } };

// ─── Color Constants ──────────────────────────────────────────────────────────

export const CHANNEL_COLORS = { sms: '#62BB46', email: '#2E8B98', whatsapp: '#62BB46', in_app: '#256F7A', voice: '#F26522' };
export const STATUS_COLORS = { queued: '#F26522', sent: '#2E8B98', delivered: '#62BB46', read: '#4FA038', failed: '#DC3545', received: '#256F7A' };
export const GRADE_COLORS = { 'A+': '#4FA038', 'A': '#62BB46', 'B': '#2E8B98', 'C': '#F26522', 'D': '#F26522', 'F': '#DC3545' };
export const DOC_TYPE_COLORS = { general: '#6C757D', proposal: '#2E8B98', contract: '#256F7A', consent: '#62BB46', insurance: '#F26522', report: '#2E8B98', invoice: '#4FA038', other: '#ADB5BD' };
export const APPT_STATUS_COLORS = { scheduled: '#2E8B98', confirmed: '#62BB46', completed: '#4FA038', cancelled: '#6C757D', no_show: '#DC3545', rescheduled: '#F26522' };
export const OUTCOME_COLORS = { answered: '#62BB46', voicemail: '#F26522', no_answer: '#DC3545', busy: '#256F7A', callback_requested: '#2E8B98' };
export const CAMPAIGN_STATUS_COLORS = { draft: '#F26522', scheduled: '#2E8B98', active: '#62BB46', paused: '#256F7A', completed: '#4FA038', cancelled: '#6C757D' };

// ─── Data Constants ───────────────────────────────────────────────────────────

export const DOC_TYPES = ['general', 'proposal', 'contract', 'consent', 'insurance', 'report', 'invoice'];

export const CONTACT_TYPES = {
  referral_physician: { label: 'Referral Physician', color: '#2E8B98' },
  insurance_rep: { label: 'Insurance Rep', color: '#256F7A' },
  vendor: { label: 'Vendor', color: '#F26522' },
  prospect: { label: 'Prospect', color: '#62BB46' },
  partner: { label: 'Partner', color: '#2E8B98' },
  other: { label: 'Other', color: '#6C757D' },
};

export const TASK_PRIORITY = {
  urgent: { label: 'Urgent', color: '#DC3545', bg: '#F8D7DA' },
  high: { label: 'High', color: '#F26522', bg: '#FFF3CD' },
  normal: { label: 'Normal', color: '#2E8B98', bg: '#CFE2FF' },
  low: { label: 'Low', color: '#6C757D', bg: '#F8F9FA' },
};

/*
 * The note shown on every workflow step that would text or email a patient.
 *
 * ⛔⛔ Card P85. These steps run and send NOTHING — the runner records them as
 * skipped with reason MESSAGING_NOT_ENABLED (routes/_workflow_run_summary.js).
 * Before that fix they reported status 'queued', so the builder showed a
 * workflow whose SMS steps looked armed and the run history showed them as
 * executed. Someone reading this screen has to be able to see that the text
 * will not go out, without running the workflow to find out.
 *
 * ⚠ Wording tracks ContactPhysicianTab's CHANNEL_UNAVAILABLE_REASON (#619) so
 * staff meet the same sentence wherever messaging is held back. The clause
 * after the colon is identical; only the lead-in differs, because there the
 * channel is unavailable to pick and here the step exists but will not send.
 */
export const MESSAGING_STEP_NOTE =
  'Not sent yet: texting and email go live with the Twilio integration.';

export const STEP_ACTIONS = {
  add_tag: { label: 'Add Tag', icon: '🏷️' },
  remove_tag: { label: 'Remove Tag', icon: '❌' },
  move_stage: { label: 'Move Stage', icon: '➡️' },
  assign_owner: { label: 'Assign Owner', icon: '👤' },
  create_task: { label: 'Create Task', icon: '✅' },
  send_sms: { label: 'Send SMS', icon: '💬', messaging: true },
  send_email: { label: 'Send Email', icon: '📧', messaging: true },
  send_whatsapp: { label: 'Send WhatsApp', icon: '💬', messaging: true },
  make_call: { label: 'Place Call', icon: '📞', messaging: true },
  wait: { label: 'Wait / Delay', icon: '⏳' },
  internal_notification: { label: 'Internal Notification', icon: '🔔' },
};

/** Would this step action put a message in front of a patient? */
export function isMessagingStep(action) {
  return !!(STEP_ACTIONS[action] && STEP_ACTIONS[action].messaging);
}

// ─── Components ───────────────────────────────────────────────────────────────

export function Badge({ text, color, bg }) {
  return <span style={{ background: bg || `${color}18`, color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{text}</span>;
}

/*
 * KPI tile.
 *
 * `onClick` is OPT-IN (card F1, 2026-08-25) and everything about this component
 * is unchanged without it. That matters: KPI is shared across the CRM, the CEO
 * dashboard and several report pages, so making every tile look or behave
 * clickable would imply a drill-down that does not exist behind most of them —
 * and only three metrics are registered server-side.
 *
 * When `onClick` IS passed the DOM shape and styles stay identical; the div gains
 * role/tabIndex/keyboard handling so it is reachable without a mouse. Rendering a
 * real <button> instead would have been the cleaner primitive, but it changes the
 * element for every existing caller's styling and tests — a bigger blast radius
 * than the accessibility gain, for a tile that is a div everywhere today.
 */
export function KPI({ icon: Icon, label, value, color = '#2E8B98', sub, onClick, clickHint }) {
  const clickable = typeof onClick === 'function';
  const activate = (e) => { if (clickable) { e.preventDefault(); onClick(); } };
  return (
    <div
      style={{ background: '#F8F9FA', borderRadius: 12, padding: '14px 18px', border: 'none', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', flex: '1 1 140px', minWidth: 130, cursor: clickable ? 'pointer' : undefined }}
      {...(clickable ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `${label} — open the rows behind this figure`,
        onClick: activate,
        // Enter and Space, because a role="button" that only responds to a mouse
        // is a button in appearance only.
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') activate(e); },
      } : {})}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        {Icon && <Icon size={14} color={color} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#212529' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#ADB5BD', marginTop: 2 }}>{sub}</div>}
      {clickable && clickHint && (
        <div style={{ fontSize: 10, color: '#2E8B98', marginTop: 4, fontWeight: 600 }}>{clickHint}</div>
      )}
    </div>
  );
}

// ─── Neumorphic Style Constants ───────────────────────────────────────────────

export const neuShadow = '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)';
export const neuShadowSm = '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.8)';
export const neuShadowInset = 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)';
export const neuShadowPressed = 'inset 4px 4px 8px rgba(186,199,216,0.45), inset -4px -4px 8px rgba(255,255,255,0.8)';

// ─── Style Objects ────────────────────────────────────────────────────────────

export const inp = { padding: '8px 12px', borderRadius: 10, border: 'none', fontSize: 13, width: '100%', background: '#F8F9FA', boxShadow: neuShadowInset, outline: 'none', color: '#212529', transition: 'box-shadow 0.15s ease' };

export const btnPrimary = {
  padding: '8px 16px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(145deg, #3CA0AE, #256F7A)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  boxShadow: `${neuShadow}, inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.1)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)', position: 'relative',
};

export const btnSm = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: '#F8F9FA', color: '#6C757D',
  cursor: 'pointer', fontSize: 12, fontWeight: 500,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: neuShadowSm,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)', position: 'relative',
};

export const btnDanger = {
  padding: '6px 12px', borderRadius: 8, border: 'none',
  background: 'linear-gradient(145deg, #E4606D, #B02A37)', color: '#fff',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  boxShadow: `${neuShadowSm}, inset 0 1px 0 rgba(255,255,255,0.2)`,
  transition: 'all 0.15s cubic-bezier(0.4,0,0.2,1)',
};

export const cardWrap = { background: '#F8F9FA', borderRadius: 14, border: 'none', overflow: 'hidden', boxShadow: neuShadow };

export const emptyMsg = { padding: 40, textAlign: 'center', color: '#ADB5BD', fontSize: 13 };

export const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };

export const modalStyle = { background: '#F8F9FA', borderRadius: 20, width: 560, maxHeight: '85vh', overflow: 'auto', padding: 28, boxShadow: '12px 12px 24px rgba(186,199,216,0.65), -12px -12px 24px rgba(255,255,255,0.9)' };

export const modalHeader = { display: 'flex', justifyContent: 'space-between', marginBottom: 20 };

export const modalTitle = { fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' };

export const closeBtn = { background: '#F8F9FA', border: 'none', cursor: 'pointer', borderRadius: 8, padding: 4, boxShadow: neuShadowSm };

export const neuCard = { background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, marginBottom: 16 };

export const neuCardInner = { background: '#F8F9FA', borderRadius: 10, boxShadow: neuShadowInset, padding: 12 };
